import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/outfit/800.css';
import '@fontsource/outfit/900.css';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { inject } from '@vercel/analytics';
import { injectSpeedInsights } from '@vercel/speed-insights';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { EnvUnsupported } from './components/EnvUnsupported';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import './i18n';
import './index.css';
import { initGA } from './lib/analytics';
import { initClarity } from './lib/clarity';

import { router } from './routes';

// Initialize analytics
initGA();
initClarity();

// Initialize Vercel Analytics and Speed Insights
inject();
injectSpeedInsights();

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((module) => ({
    default: module.ReactQueryDevtools,
  }))
);

const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((module) => ({
    default: module.NotFoundPage,
  }))
);

function AppErrorFallback({ error }: { error: unknown }) {
  if (import.meta.env.DEV) {
    console.error('Application Error:', error);
  }
  return (
    <Suspense fallback={null}>
      <NotFoundPage />
    </Suspense>
  );
}

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'mmbox-query-cache',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      retry: 2,
      refetchOnWindowFocus: true,
    },
  },
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

// Check if already rendered to prevent double mounting
let root: ReturnType<typeof createRoot> | null = null;

const initApp = () => {
  if (root) {
    // Already rendered, just update
    return;
  }

  root = createRoot(rootElement);

  try {
    root.render(
      <StrictMode>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: 1000 * 60 * 60 * 24, // 24 hours
            dehydrateOptions: {
              shouldDehydrateQuery: (query) => {
                // Only persist home page related queries and tags
                const key = query.queryKey;
                const persistableKeys = ['tags', 'home', 'top5', 'trending'];
                return (
                  query.state.status === 'success' &&
                  key.some(
                    (k: unknown) =>
                      typeof k === 'string' && persistableKeys.includes(k)
                  )
                );
              },
            },
          }}
        >
          <AuthProvider>
            <ErrorBoundary fallback={AppErrorFallback}>
              <RouterProvider router={router} />
            </ErrorBoundary>
            <ToastContainer
              position="top-center"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
            />
          </AuthProvider>
          {import.meta.env.DEV && (
            <Suspense fallback={null}>
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-left"
              />
            </Suspense>
          )}
          {import.meta.env.DEV && (
            <div
              className="fixed top-3 left-1/2 transform -translate-x-1/2 bg-red-500 text-white font-bold text-xs px-4 py-2 rounded shadow-lg"
              title="Development Mode"
            >
              <small>DEV</small>
            </div>
          )}
        </PersistQueryClientProvider>
      </StrictMode>
    );
  } catch {
    root?.render(<EnvUnsupported />);
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
